import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Chip,
} from "@mui/material";
import { ReactNode } from "react";

/**
 * CountingFilterSelect — a Select dropdown whose options display a live count
 * of how many rows match each value. Options with zero matches are visually
 * dimmed so the user knows they exist but are currently empty (instead of
 * silently returning 0 results and looking like a broken filter).
 *
 * Used across every list page so filter UX is predictable:
 *   "Where in QB" no longer "returns nothing" — the dropdown shows
 *   "Charge (109)" "Credit (0, dimmed)" "Drift (0, dimmed)" etc.
 *
 * Props:
 * - label: Select input label
 * - value: current selected value
 * - onChange: value change handler
 * - options: list of { value, label, count? }
 * - allLabel: display text for the "all" sentinel option (defaults to "All")
 * - minWidth: the FormControl minWidth (defaults to 160)
 */

export interface FilterOption {
  value: string;
  label: string;
  /**
   * How many rows would match this filter value right now. Leave undefined
   * to hide the count (e.g. for server-side filters where the shell can't
   * compute counts cheaply).
   */
  count?: number;
}

interface Props {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: FilterOption[];
  allLabel?: string;
  minWidth?: number;
  icon?: ReactNode;
}

export function CountingFilterSelect({
  label,
  value,
  onChange,
  options,
  allLabel = "All",
  minWidth = 180,
  icon,
}: Props) {
  const totalCount = options.reduce((sum, o) => sum + (o.count ?? 0), 0);
  return (
    <FormControl size="small" sx={{ minWidth }}>
      <InputLabel>{label}</InputLabel>
      <Select
        value={value}
        label={label}
        onChange={(e) => onChange(e.target.value)}
        renderValue={(selected) => {
          if (selected === "all") return allLabel;
          const match = options.find((o) => o.value === selected);
          return match?.label ?? String(selected);
        }}
      >
        <MenuItem value="all">
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: 2 }}>
            <Typography variant="body2">{allLabel}</Typography>
            {totalCount > 0 && (
              <Chip
                size="small"
                label={totalCount}
                sx={{
                  height: 18,
                  fontSize: "0.7rem",
                  bgcolor: "var(--c-overlay-6)",
                  color: "var(--c-text-secondary)",
                }}
              />
            )}
          </Box>
        </MenuItem>
        {options.map((opt) => {
          const count = opt.count;
          const isEmpty = count === 0;
          return (
            <MenuItem
              key={opt.value}
              value={opt.value}
              disabled={isEmpty}
              sx={{ opacity: isEmpty ? 0.55 : 1 }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  gap: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {icon}
                  <Typography variant="body2">{opt.label}</Typography>
                </Box>
                {typeof count === "number" && (
                  <Chip
                    size="small"
                    label={count}
                    sx={{
                      height: 18,
                      fontSize: "0.7rem",
                      bgcolor: isEmpty ? "var(--c-overlay-4)" : "var(--c-overlay-6)",
                      color: isEmpty ? "var(--c-text-muted)" : "var(--c-text-secondary)",
                    }}
                  />
                )}
              </Box>
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
}
